import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { LoadingIndicator, LoadingSpinner } from '../../components/onboarding/LoadingIndicator';
import {
  CalendarDays,
  CheckCircle2,
  Pencil,
  Play,
  Trash2,
  Users,
} from 'lucide-react';
import {
  Icon,
  MenuItem,
  Select,
  styled,
  Typography,
} from '@mui/material';
import projectDropdownArrow from '../../assets/project-dropdown-arrow.svg';
import { ActiveOrganizationNotice } from '../../components/onboarding/ActiveOrganizationNotice';
import OnboardingSubHeader from '../../components/onboarding/OnboardingSubHeader';
import { OnboardingSurface } from '../../components/onboarding/Surface';
import { Button } from '../../components/onboarding/Button';
import { Dialog } from '../../components/onboarding/Dialog';
import { Input } from '../../components/onboarding/Input';
import { InfrastructureStatusNotice } from '../../components/onboarding/InfrastructureStatusNotice';
import { ProjectRunsModal } from '../../components/onboarding/ProjectRunsModal';
import {
  DEFAULT_PROJECT_PAGE_SIZE,
  deleteProject,
  listProjects,
  listProjectsProvisionStatus,
} from '../../services/onboardingApi';
import type {
  BackendAssigneePreview,
  BackendProjectDTO,
} from '../../services/onboardingTypes';
import { backendProjectToIProject } from '../../services/onboardingTransforms';
import { downloadPerformanceReport, downloadQueryLogReport } from '../../redux/slice/viewReportSlice';
import { pageRoutes } from '../../constants/routes';
import { IProject, setProjectAndRun } from '../../redux/slice/userProjectSlice';
import { setShowNotification } from '../../redux/slice/snackBarSlice';
import { useAppDispatch } from '../../redux/store';
import { useAuth } from '../../contexts/AuthContext';
import { useDebouncedValue } from '../../lib/hooks/useDebouncedValue';
import { usePaginatedList } from '../../lib/hooks/usePaginatedList';
import {
  canUpdateProjectFields,
  formatUserDisplayName,
  isPlatformAdminUser,
  isProjectInfrastructureReady,
  isProvisioningActive,
  normalizeProjectProvisionStatus,
  ProjectProvisionStatus,
} from '../../types/onboarding';
import { UIColors } from '../../constants/UIConstants';
import { pxToRem } from '../../utils/getFontValue';
import { twMerge as cn } from 'tailwind-merge';

const ORG_FILTER_MAX_WIDTH = pxToRem(240);
const ASSIGNEE_PLATE_MAX_WIDTH = pxToRem(168);

const StyledFilterSelect = styled(Select)({
  minWidth: pxToRem(200),
  maxWidth: ORG_FILTER_MAX_WIDTH,
  width: ORG_FILTER_MAX_WIDTH,
  height: pxToRem(40),
  color: UIColors.primary.navyBlue,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  '& .MuiTypography-root': {
    fontSize: pxToRem(14),
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

function orgFilterMenuPaperSx(menuWidth?: number) {
  const width = menuWidth ?? ORG_FILTER_MAX_WIDTH;
  return {
    color: UIColors.primary.navyBlue,
    width,
    maxWidth: width,
    minWidth: width,
    overflow: 'hidden',
    bgcolor: 'background.paper',
    '& .MuiMenuItem-root': {
      overflow: 'hidden',
      maxWidth: '100%',
    },
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
      display: 'block',
    },
  };
}

const StyledDropdownIcon = styled(Icon)({
  borderRadius: pxToRem(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function formatStartDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
      new Date(iso)
    );
  } catch {
    return iso;
  }
}

function FilterDropdownIcon(props: Record<string, unknown>) {
  return (
    <StyledDropdownIcon {...props}>
      <img alt="" src={projectDropdownArrow} />
    </StyledDropdownIcon>
  );
}

function OrgFilterLabel({ text }: { text: string }) {
  return (
    <Typography component="span" noWrap title={text}>
      {text}
    </Typography>
  );
}

function AssigneeNamePlate({ label }: { label: string }) {
  return (
    <span
      title={label}
      className="inline-block truncate rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
      style={{ maxWidth: ASSIGNEE_PLATE_MAX_WIDTH }}
    >
      {label}
    </span>
  );
}

function assigneesFromPreview(
  preview: BackendAssigneePreview[] | undefined
): { userId: number; label: string }[] {
  if (!preview?.length) return [];
  const byUserId = new Map<number, { userId: number; label: string }>();
  for (const user of preview) {
    if (byUserId.has(user.user_id)) continue;
    byUserId.set(user.user_id, {
      userId: user.user_id,
      label:
        formatUserDisplayName(user.first_name, user.last_name, '') ||
        user.email_id,
    });
  }
  return Array.from(byUserId.values());
}

type ProjectCard = IProject & {
  assignees: { userId: number; label: string }[];
};

const PROJECT_UPDATED_SUCCESS = 'project-updated';

function ViewProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const {
    currentUser,
    organizations,
    ensureOrganizations,
    orgContextVersion,
    isLoading: authLoading,
  } = useAuth();

  const isPlatformAdmin = isPlatformAdminUser(currentUser);
  const canEditProjects = Boolean(
    currentUser && canUpdateProjectFields(currentUser.platformRole),
  );

  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [orgFilterMenuWidth, setOrgFilterMenuWidth] = useState<number>();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [provisionOverrides, setProvisionOverrides] = useState<
    Record<number, { provision_status?: ProjectProvisionStatus; provision_error?: string }>
  >({});

  const [runsOpen, setRunsOpen] = useState(false);
  const [runsProject, setRunsProject] = useState<IProject | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IProject | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showProjectUpdatedSuccess, setShowProjectUpdatedSuccess] =
    useState(false);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void ensureOrganizations();
  }, [ensureOrganizations, orgContextVersion]);

  const orgFilterOptions = useMemo(
    () =>
      organizations
        .map((org) => ({ id: String(org.id), name: org.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [organizations]
  );

  const organizationIdFilter = useMemo(() => {
    if (!isPlatformAdmin || orgFilter === 'all') return undefined;
    const numericId = Number(orgFilter);
    if (
      Number.isFinite(numericId) &&
      !Number.isNaN(numericId) &&
      numericId > 0
    ) {
      return numericId;
    }
    return undefined;
  }, [isPlatformAdmin, orgFilter]);

  const listFilters = useMemo(
    () => ({
      organizationId: organizationIdFilter,
    }),
    [organizationIdFilter]
  );

  const fetchProjectsPage = useCallback(
    async ({
      pageNumber,
      pageSize,
      search,
      signal,
      organizationId,
    }: {
      pageNumber: number;
      pageSize: number;
      search?: string;
      signal?: AbortSignal;
      organizationId?: number;
    }) => {
      const response = await listProjects({
        pageNumber,
        pageSize,
        search,
        signal,
        organizationId:
          typeof organizationId === 'number' ? organizationId : undefined,
        includeAssignees: true,
      });
      return {
        items: response.projects,
        page_number: response.page_number,
        page_size: response.page_size,
        total_count: response.total_count,
        has_more: response.has_more,
      };
    },
    []
  );

  const {
    items: projectDtos,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    error,
    loadMore,
    removeItem,
  } = usePaginatedList<BackendProjectDTO>({
    pageSize: DEFAULT_PROJECT_PAGE_SIZE,
    fetchPage: fetchProjectsPage,
    filters: listFilters,
    search: debouncedSearch,
    enabled: !authLoading && Boolean(currentUser),
    getItemKey: (item) => item.project_id,
  });

  const displayedProjects = useMemo((): ProjectCard[] => {
    return projectDtos.map((dto) => {
      const base = backendProjectToIProject(dto);
      const override = provisionOverrides[dto.project_id];
      return {
        ...base,
        provision_status: override?.provision_status ?? base.provision_status,
        provision_error: override?.provision_error ?? base.provision_error,
        assignees: assigneesFromPreview(dto.assignee_preview),
      };
    });
  }, [projectDtos, provisionOverrides]);

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const isLoadingMoreRef = useRef(isLoadingMore);
  isLoadingMoreRef.current = isLoadingMore;
  const loadMoreFnRef = useRef(loadMore);
  loadMoreFnRef.current = loadMore;
  const hasDisplayedProjects = displayedProjects.length > 0;

  useEffect(() => {
    if (searchParams.get('success') !== PROJECT_UPDATED_SUCCESS) return;
    setShowProjectUpdatedSuccess(true);
    const next = new URLSearchParams(searchParams);
    next.delete('success');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const provisioningProjectIdsKey = useMemo(() => {
    return projectDtos
      .filter((dto) => {
        const override = provisionOverrides[dto.project_id];
        const status =
          override?.provision_status ??
          normalizeProjectProvisionStatus(dto.provision_status);
        return isProvisioningActive(status);
      })
      .map((dto) => dto.project_id)
      .sort((a, b) => a - b)
      .join(',');
  }, [projectDtos, provisionOverrides]);

  useEffect(() => {
    if (!provisioningProjectIdsKey) return;

    const projectIds = provisioningProjectIdsKey
      .split(',')
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    const poll = () => {
      void listProjectsProvisionStatus(projectIds)
        .then((res) => {
          setProvisionOverrides((prev) => {
            const next = { ...prev };
            for (const row of res.statuses ?? []) {
              next[row.project_id] = {
                provision_status: normalizeProjectProvisionStatus(
                  row.provision_status
                ),
                provision_error: row.provision_error,
              };
            }
            return next;
          });
        })
        .catch(() => {
          /* best-effort poll */
        });
    };

    poll();
    const intervalId = window.setInterval(poll, 20000);
    return () => window.clearInterval(intervalId);
  }, [provisioningProjectIdsKey]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasDisplayedProjects) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (
          isLoadingRef.current ||
          isLoadingMoreRef.current ||
          !hasMoreRef.current
        ) {
          return;
        }
        loadMoreFnRef.current();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasDisplayedProjects]);

  const openRuns = (project: IProject) => {
    setRunsProject(project);
    setRunsOpen(true);
  };

  const closeRuns = () => {
    setRunsOpen(false);
    setRunsProject(null);
  };

  const handleEditProject = (project: IProject) => {
    navigate(pageRoutes.onboardingProjectById(project.project_id));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const targetName = deleteTarget.project_name;
    const deletedId = deleteTarget.project_id;
    try {
      await deleteProject(deletedId);
      setDeleteTarget(null);
      removeItem(deletedId);
      setProvisionOverrides((prev) => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });
      dispatch(
        setShowNotification(`Project "${targetName}" has been deleted.`),
      );
    } catch (err: unknown) {
      const status =
        err != null &&
        typeof err === 'object' &&
        'response' in err &&
        err.response != null &&
        typeof err.response === 'object' &&
        'status' in err.response
          ? (err.response as { status: number }).status
          : null;

      setDeleteTarget(null);
      if (status === 404) {
        removeItem(deletedId);
        setProvisionOverrides((prev) => {
          const next = { ...prev };
          delete next[deletedId];
          return next;
        });
        dispatch(
          setShowNotification(`Project "${targetName}" has already been deleted.`),
        );
      } else {
        dispatch(
          setShowNotification(
            `Failed to delete project "${targetName}". Please try again.`,
          ),
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const orgFilterLabel =
    orgFilter === 'all'
      ? 'All organizations'
      : orgFilterOptions.find((o) => String(o.id) === orgFilter)?.name ??
        'Organization';

  const showInitialLoader = authLoading || (isLoading && displayedProjects.length === 0);
  const hasSearchOrFilter =
    debouncedSearch.trim().length > 0 || orgFilter !== 'all';

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setOrgFilter('all');
  }, []);

  const showOrgFilter =
    isPlatformAdmin && orgFilterOptions.length > 0;

  return (
    <>
      <OnboardingSubHeader title="View projects" showBackButton />

      <main className="bg-surface-muted px-6 py-8 sm:px-10 lg:py-10">
        <div className="mx-auto max-w-6xl">
          {showProjectUpdatedSuccess && (
            <div className="onboarding-success-banner mb-8 flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="text-green-800">
                  Project updated successfully!
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowProjectUpdatedSuccess(false)}
                className="text-green-600 hover:text-green-800"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="mb-8">
            <div className="mb-6">
              <h1 className="mb-2 text-2xl font-bold text-brand sm:text-3xl">
                {isPlatformAdmin ? 'All projects' : 'Your projects'}
              </h1>
              <p className="max-w-3xl text-gray-600">
                {canEditProjects
                  ? 'Open runs for a project, or use the pencil to edit dates and team membership.'
                  : 'Open runs for projects you are assigned to.'}
              </p>
              {!isPlatformAdmin && (
                <ActiveOrganizationNotice className="mt-3 max-w-3xl" />
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative min-w-0 flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    isPlatformAdmin
                      ? 'Search by project or organization name...'
                      : 'Search by project name...'
                  }
                  className="pl-10"
                  aria-label="Search projects"
                />
              </div>
              {showOrgFilter && (
                <StyledFilterSelect
                  IconComponent={FilterDropdownIcon}
                  value={orgFilter}
                  variant="outlined"
                  sx={{
                    width: { xs: '100%', sm: ORG_FILTER_MAX_WIDTH },
                    maxWidth: ORG_FILTER_MAX_WIDTH,
                    flexShrink: 0,
                  }}
                  onChange={(e) => setOrgFilter(String(e.target.value))}
                  onOpen={(event) => {
                    setOrgFilterMenuWidth(
                      (event.currentTarget as HTMLElement).clientWidth,
                    );
                  }}
                  onClose={() => setOrgFilterMenuWidth(undefined)}
                  renderValue={() => (
                    <OrgFilterLabel text={orgFilterLabel} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: orgFilterMenuPaperSx(orgFilterMenuWidth),
                    },
                  }}
                >
                  <MenuItem value="all">
                    <OrgFilterLabel text="All organizations" />
                  </MenuItem>
                  {orgFilterOptions.map((org) => (
                    <MenuItem key={org.id} value={String(org.id)}>
                      <OrgFilterLabel text={org.name} />
                    </MenuItem>
                  ))}
                </StyledFilterSelect>
              )}
            </div>

            {!showInitialLoader && (totalCount > 0 || hasSearchOrFilter) && (
              <p className="mt-3 text-sm text-gray-500">
                {totalCount} project{totalCount === 1 ? '' : 's'}
                {hasSearchOrFilter ? ' matching your filters' : ''}
              </p>
            )}
          </div>

          {showInitialLoader && (
            <LoadingIndicator
              label="Loading projects..."
              layout="page"
              className="min-h-[50vh]"
            />
          )}

          {!showInitialLoader && error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!showInitialLoader && !error && displayedProjects.length === 0 && (
            <OnboardingSurface className="p-10 text-center">
              <p className="text-gray-600">
                {hasSearchOrFilter
                  ? 'No projects match your search or filters.'
                  : 'No projects are available for your account. If you expected to see projects here, contact your administrator.'}
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-6 border-brand text-brand"
                onClick={() =>
                  hasSearchOrFilter ? clearFilters() : navigate(pageRoutes.onboarding)
                }
              >
                {hasSearchOrFilter ? 'Clear filters' : 'Back to onboarding'}
              </Button>
            </OnboardingSurface>
          )}

          {!showInitialLoader && !error && displayedProjects.length > 0 && (
            <>
              <div className="grid gap-6 sm:grid-cols-2">
                {displayedProjects.map((project) => {
                  const infraReady = isProjectInfrastructureReady(
                    project.provision_status
                  );
                  return (
                    <OnboardingSurface
                      key={project.project_id}
                      className="flex flex-col overflow-hidden shadow-md"
                    >
                      <div className="flex flex-1 flex-col p-6">
                        <div className="mb-4 flex items-start justify-between gap-3">
                          <h2
                            className="line-clamp-2 min-w-0 flex-1 text-lg font-bold text-brand"
                            title={project.project_name}
                          >
                            {project.project_name}
                          </h2>
                          {canEditProjects && (
                            <div className="flex shrink-0 items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleEditProject(project)}
                                disabled={isProvisioningActive(project.provision_status)}
                                title={
                                  isProvisioningActive(project.provision_status)
                                    ? 'Infrastructure is being provisioned — editing is locked until it completes'
                                    : undefined
                                }
                                className={cn(
                                  'rounded-md p-2',
                                  isProvisioningActive(project.provision_status)
                                    ? 'cursor-not-allowed text-gray-300'
                                    : 'text-brand hover:bg-gray-100'
                                )}
                                aria-label={`Edit ${project.project_name}`}
                              >
                                <Pencil className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(project)}
                                className="rounded-md p-2 text-red-600 hover:bg-red-50"
                                aria-label={`Delete ${project.project_name}`}
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="mb-6 space-y-4 text-sm">
                          {isPlatformAdmin && (
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                Customer name
                              </p>
                              <p
                                className="mt-1 truncate font-medium text-gray-900"
                                title={project.client_org_name}
                              >
                                {project.client_org_name}
                              </p>
                            </div>
                          )}
                          <div className="flex items-start gap-2 text-gray-600">
                            <CalendarDays
                              className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                              aria-hidden
                            />
                            <span>{formatStartDate(project.project_start_date)}</span>
                          </div>
                          <div className="flex min-w-0 items-start gap-2">
                            <Users
                              className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap gap-1.5">
                              {project.assignees.length === 0 ? (
                                <span className="text-xs text-gray-500">
                                  No assignees
                                </span>
                              ) : (
                                project.assignees.map((a) => (
                                  <AssigneeNamePlate
                                    key={`${project.project_id}-${a.userId}`}
                                    label={a.label}
                                  />
                                ))
                              )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto space-y-2">
                          {!infraReady && (
                            <InfrastructureStatusNotice
                              status={project.provision_status}
                              provisionError={project.provision_error}
                              compact
                            />
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!infraReady}
                            className={cn(
                              'h-11 w-full gap-2 border-2',
                              infraReady
                                ? 'border-brand bg-white text-brand hover:bg-brand/5'
                                : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                            )}
                            onClick={() => openRuns(project)}
                          >
                            <Play className="h-4 w-4 fill-current" aria-hidden />
                            View Runs
                          </Button>
                        </div>
                      </div>
                    </OnboardingSurface>
                  );
                })}
              </div>

              <div ref={loadMoreRef} className="mt-8 flex justify-center py-4">
                {isLoadingMore && (
                  <LoadingSpinner size="lg" className="text-brand" />
                )}
                {!hasMore && displayedProjects.length > 0 && (
                  <p className="text-sm text-gray-500">All projects loaded.</p>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <ProjectRunsModal
        open={runsOpen && runsProject != null}
        onClose={closeRuns}
        projectId={runsProject?.project_id ?? null}
        customerName={runsProject?.client_org_name ?? ''}
        projectName={runsProject?.project_name ?? ''}
        onViewRun={(runId) => {
          if (!runsProject) return;
          dispatch(setProjectAndRun({ project: runsProject, runId }));
          navigate(pageRoutes.dashboardProjectRun(runsProject.project_id, runId));
          closeRuns();
        }}
        onDownloadPerformanceReport={async (runId) => {
          if (!runsProject) return;
          await dispatch(
            downloadPerformanceReport(runsProject.project_id, runId)
          );
        }}
        onDownloadQueryLogReport={async (runId) => {
          if (!runsProject) return;
          await dispatch(downloadQueryLogReport(runsProject.project_id, runId));
        }}
      />

      <Dialog
        open={deleteTarget != null}
        onClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        labelledBy="delete-project-dialog-title"
        panelClassName="max-w-md"
      >
        <h2
          id="delete-project-dialog-title"
          className="text-lg font-bold text-brand"
        >
          Delete project?
        </h2>
        <p className="mt-3 text-sm text-gray-600">
          Are you sure you want to permanently delete{' '}
          <span className="break-words font-semibold">
            {deleteTarget?.project_name}
          </span>?
        </p>
        <p className="mt-2 text-sm text-gray-600">
          This will delete all project data including runs, queries, and cloud
          infrastructure (S3 bucket, databases, and secrets). This action cannot
          be undone.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Deleting…
              </span>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </Dialog>
    </>
  );
}

export default ViewProjectsPage;
